/**
 * ExecutiveReportPage — V2 dashboard-style executive report.
 *
 * Fixed timeframe (Yesterday). Displays 5 sections: excess alarms table,
 * reason chart, care setting averages, response distribution charts,
 * and top 10 locations. Uses AlertDetails ReportFilterDrawer for filters.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getUsername,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, getToday, campusNow } from '../../../libs.jsx';
import {
    fetchExecutiveReport, fetchExecutiveFilterOptions, saveExecThreshold,
} from './api/executiveApi.js';
import { addEventNote, editEventReason, fetchPreformNotes } from '../alert-details/api/alertDetailsApi.js';
import { fetchCallLogsReport } from '../call-logs/api/callLogsReportApi.js';

import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import EventNoteDialog from '../../../components/v2/EventNoteDialog.jsx';
import EditReasonDialog from '../alert-details/components/EditReasonDialog.jsx';
import ExcessAlarmsTable from './components/ExcessAlarmsTable.jsx';
import ReasonChart from './components/ReasonChart.jsx';
import CareSettingTable from './components/CareSettingTable.jsx';
import ResponseDistribution from './components/ResponseDistribution.jsx';
import TopLocationsTable from './components/TopLocationsTable.jsx';

import './executive-report-page.css';
import { expandReasonIds } from '../../../utils/reasonFilterIds.js';
import { REPORT_TYPE_ID } from '../../../utils/reportTypes.js';
import { buildFilterChips, countActiveFilters } from '../../../utils/reportFilterChips.js';
import { reportErrorMessage, clearCampusFilters } from '../../../utils/reportPageState.js';

/* Where the Response Time filter actually applies, shown on its chip so the report
   never claims more than it does. Every section honours it except the Excess Alarms
   table, which uses its own "Alarms Exceeding" threshold: the reason chart's proc
   always took response bounds, and the top-areas and care-setting sections gained
   them with the sp_get* procs (docs/sp_executive_sections_v1.sql; audit A9 / A28). */
const RESPONSE_TIME_SCOPE = 'all sections except Excess Alarms';

function emptyReportFilters() {
    return {
        areas: [], areaGroups: [], areaTypes: [],
        residents: [], residentGroups: [],
        users: [], userGroups: [],
        deviceCategories: [], devices: [],
        eventTypes: [], reasons: [],
        responseTime: { enabled: false, operator: 'lt', value: 60 },
    };
}

const ExecutiveReportPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);

    // -- Report state --
    const [status, setStatus] = useState('init');
    const [excessiveAlerts, setExcessiveAlerts] = useState([]);
    const [reasonData, setReasonData] = useState({});
    const [areaTypesResponse, setAreaTypesResponse] = useState([]);
    const [areaTypesChart, setAreaTypesChart] = useState({});
    const [dailyAreas, setDailyAreas] = useState([]);

    // -- Session feature flags --
    const eventNotesActive = Boolean(campusConfig?.event_notes_active);
    const eventNotesPreform = Boolean(campusConfig?.event_notes_preform);
    const eventNotesFreeform = Boolean(campusConfig?.event_notes_freeform);
    const isReasonEditable = Boolean(campusConfig?.is_reason_editable);
    // Grandstream campuses get a Calls column (call logs merged into events by event_id).
    const isGrandstream = Boolean(campusConfig?.is_grandstream);

    // -- Note / Reason dialog state --
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteTarget, setNoteTarget] = useState(null);
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [reasonTarget, setReasonTarget] = useState(null);
    const [prenoteData, setPrenoteData] = useState([]);
    const [saving, setSaving] = useState(false);

    // -- Exec threshold --
    const defaultExecTime = campusConfig?.exec_excess_time || 20;
    const [execTime, setExecTime] = useState(defaultExecTime);
    const [savingThreshold, setSavingThreshold] = useState(false);

    // -- Navigation state (from ScheduledReportsPage "Run Report Local") --
    const location = useLocation();
    const navStateRef = useRef(location.state);
    const navState = navStateRef.current;

    // -- Filters — pre-fill from navigation state if present --
    const [reportFilters, setReportFilters] = useState(() => {
        if (navState?.filters) return { ...emptyReportFilters(), ...navState.filters };
        return emptyReportFilters();
    });
    const [filterOptions, setFilterOptions] = useState({});
    // True once the filter-options fetch has finished, whether or not it succeeded.
    const [optionsSettled, setOptionsSettled] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Time range (fixed Yesterday) --
    const [rangeId] = useState(2);
    const [rangeName] = useState('Yesterday');
    const [start] = useState(() => { const d = getToday(-1); d.setHours(0,0,0,0); return d; });
    const [end] = useState(() => { const d = getToday(-1); d.setHours(23,59,59,999); return d; });

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- Toast --
    const { toast, showToast, dismissToast } = useToast();
    const shouldRerunRef = useRef(false);
    const autoRunRef = useRef(true);

    // -- Load filter options + preform notes on mount / campus change --
    useEffect(() => {
        if (!campusId) return;
        setOptionsSettled(false);
        fetchExecutiveFilterOptions(campusId)
            .then((opts) => { setFilterOptions(opts); setOptionsSettled(true); })
            .catch(() => setOptionsSettled(true));
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
        if (eventNotesActive) {
            fetchPreformNotes(campusId).then(setPrenoteData);
        }
    }, [campusId, eventNotesActive]);

    // -- Auto-run on first load: fired below handleRunReport (see there). --

    // -- Run Report --
    const handleRunReport = useCallback(async () => {
        setStatus('loading');

        // Cap end to now, in campus time like `end` itself (campusNow, libs.jsx): a browser
        // clock behind the campus would clip the campus's latest alerts.
        const now = campusNow();
        const endToUse = end > now ? now : end;
        const stD = formatDateIso(start);
        const enD = formatDateIso(endToUse);

        // Drawer keys reasons by cloud_reason_code_id; combine with each row's
        // matching reason_code_id.
        const combinedReasonIds = expandReasonIds(reportFilters.reasons, filterOptions.reasonCodes);
        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: combinedReasonIds.join(','),
            /* Same shape the other report pages send. Applies to every section EXCEPT
               the Excess Alarms table, which keeps its own "Alarms Exceeding" threshold
               (excessive_filter). Every other section honours it — see
               RESPONSE_TIME_SCOPE above. */
            responseTime: reportFilters.responseTime?.enabled
                ? [true, reportFilters.responseTime.operator, reportFilters.responseTime.value]
                : [false, 'lt', 60],
        };

        const result = await fetchExecutiveReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            filters: filterPayload,
            execTime,
            isCloud: Boolean(campusConfig?.is_cloud),
        });

        if (result.ErrorCode) {
            showToast('error', reportErrorMessage(result.ErrorCode));
            setStatus('init');
            return;
        }

        let excessAlerts = result.excessiveAlerts || [];

        // Grandstream campuses: fetch call logs for the same window and merge them
        // into the excess-alarm events by event_id (mirrors how notes ride on each
        // row) so the table can show a per-event call count + hover. Non-fatal on
        // failure — the report still renders, just without call badges. Call logs
        // proc wants DATE strings (formatDateIso already yields yyyy-mm-dd).
        if (isGrandstream && excessAlerts.length) {
            try {
                const callResp = await fetchCallLogsReport({
                    campusId,
                    startDate: stD,
                    endDate: enD,
                });
                const callRecords = callResp?.records || [];
                if (callRecords.length) {
                    const callsByEvent = new Map();
                    callRecords.forEach((c) => {
                        const key = String(c.event_id ?? '');
                        if (!key) return;
                        if (!callsByEvent.has(key)) callsByEvent.set(key, []);
                        callsByEvent.get(key).push(c);
                    });
                    callsByEvent.forEach((arr) => arr.sort(
                        (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
                    ));
                    excessAlerts = excessAlerts.map((r) => ({
                        ...r,
                        calls: callsByEvent.get(String(r.event_id ?? '')) || [],
                    }));
                }
            } catch (err) {
                console.error('[ExecutiveReportPage] Call logs merge failed:', err);
            }
        }

        setExcessiveAlerts(excessAlerts);
        setReasonData(result.reasonData || {});
        setAreaTypesResponse(result.areaTypesResponse || []);
        setAreaTypesChart(result.areaTypesResponseChart?.chart || {});
        setDailyAreas(result.dailyAreas || []);
        setStatus('ready');
    }, [filterOptions, campusId, start, end, reportFilters, execTime, showToast, isGrandstream]);

    /* Fire the armed auto-run. Held until the filter options have settled when the
       run carries a reason filter: the request expands reasons to cloud + local ids
       from those options (utils/reasonFilterIds.js), and firing first would silently
       send cloud ids only and return fewer rows. Without a reason filter it runs
       at once, as before. Declared below handleRunReport so its dep array can list it. */
    useEffect(() => {
        if (!autoRunRef.current) return;
        if ((reportFilters.reasons || []).length > 0 && !optionsSettled) return;
        autoRunRef.current = false;
        handleRunReport();
    }, [optionsSettled, reportFilters, handleRunReport]);

    // -- Re-run after filter apply --
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    // -- Campus change --
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        /* The filter lists hold the old campus's ids, which would still be sent (A21). */
        setReportFilters(clearCampusFilters);
        setStatus('init');
        setExcessiveAlerts([]);
        setReasonData({});
        setAreaTypesResponse([]);
        setAreaTypesChart({});
        setDailyAreas([]);
        const s = getSession();
        const newCfg = s?.campusData?.[val.toString()];
        setExecTime(newCfg?.exec_excess_time || 20);
    }, []);

    // -- Save threshold --
    const handleSaveThreshold = useCallback(async () => {
        setSavingThreshold(true);
        const result = await saveExecThreshold(campusId, execTime, defaultExecTime, getUserId(), getSessionId());
        setSavingThreshold(false);
        if (result[0]) {
            showToast('success', `Threshold saved to ${execTime} minutes`);
            // Update session
            const s = getSession();
            if (s?.campusData?.[campusId.toString()]) {
                s.campusData[campusId.toString()].exec_excess_time = execTime;
            }
        } else {
            showToast('error', result[1] || 'Failed to save threshold');
        }
    }, [campusId, execTime, defaultExecTime, showToast]);

    // -- Filter handlers --
    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    // Filter chips
    /* utils/reportFilterChips.js — names resolved per sub-section idKey (audit B16). */
    const reportFilterChips = useMemo(() => buildFilterChips(reportFilters, filterOptions, null, { responseTimeScope: RESPONSE_TIME_SCOPE }), [reportFilters, filterOptions]);

    /* Active filters, not selected items — the same badge on every report (audit A22). */
    const activeFilterCount = useMemo(() => countActiveFilters(reportFilters), [reportFilters]);

    // -- Note dialog handlers --
    const handleOpenNoteDialog = useCallback((event) => {
        setNoteTarget(event);
        setNoteDialogOpen(true);
    }, []);

    const handleSaveNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Note added successfully.');
            setNoteDialogOpen(false);
            setNoteTarget(null);
            handleRunReport();
        } else {
            showToast('error', result[1] || 'Failed to add note.');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Reason dialog handlers --
    const handleOpenReasonDialog = useCallback((event) => {
        setReasonTarget(event);
        setReasonDialogOpen(true);
    }, []);

    const handleSaveReason = useCallback(async (eventId, newReasonId, oldReasonId) => {
        setSaving(true);
        const result = await editEventReason(eventId, newReasonId, oldReasonId, campusId, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Reason updated successfully.');
            setReasonDialogOpen(false);
            setReasonTarget(null);
            handleRunReport();
        } else {
            showToast('error', result[1] || 'Failed to update reason.');
        }
    }, [campusId, showToast, handleRunReport]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('success', 'Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast('error', result[1] || 'Failed to create schedule.'); }
    }, [campusId, showToast]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Executive</span>
                                </div>
                                <div className="ps-campus-row">
                                    <label htmlFor="ex-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="ex-campus-selector" value={campusId} onChange={handleCampusChange} className="ps-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">Executive Report</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setScheduleDrawerOpen(true)}>
                                        <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        {/* Filter chips */}
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (
                                    <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                                ))}
                                <button className="ar-filter-chips__clear" onClick={handleClearReportFilters}>Clear All</button>
                            </div>
                        ) : null}

                        {/* Init */}
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Click <strong>Run Report</strong> to generate yesterday's executive summary.</p>
                            </div>
                        ) : null}

                        {/* Loading */}
                        {status === 'loading' ? (
                            <div className="ar-loading"><div className="ar-loading__spinner" /><p>Loading executive report...</p></div>
                        ) : null}

                        {/* Report sections */}
                        {status === 'ready' ? (
                            <div className="ex-sections">
                                <ExcessAlarmsTable
                                    data={excessiveAlerts}
                                    execTime={execTime}
                                    onExecTimeChange={setExecTime}
                                    onSaveThreshold={handleSaveThreshold}
                                    savingThreshold={savingThreshold}
                                    tzOffset={tzOffset}
                                    onAddNote={handleOpenNoteDialog}
                                    onEditReason={handleOpenReasonDialog}
                                    eventNotesActive={eventNotesActive}
                                    isReasonEditable={isReasonEditable}
                                    isGrandstream={isGrandstream}
                                />
                                <ReasonChart reasonData={reasonData} />
                                <CareSettingTable data={areaTypesResponse} />
                                <ResponseDistribution chartData={areaTypesChart} />
                                <TopLocationsTable data={dailyAreas} />
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer — reuse AlertDetails drawer, hide time range */}
                    <ReportFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        hideTimeRange={true}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={REPORT_TYPE_ID.EXECUTIVE}
                        rangeId={rangeId}
                        rangeName={rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteTarget ? (
                        <EventNoteDialog
                            event={noteTarget}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteTarget(null); }}
                            onSave={handleSaveNote}
                            saving={saving}
                        />
                    ) : null}

                    {/* Edit Reason Dialog */}
                    {reasonDialogOpen && reasonTarget ? (
                        <EditReasonDialog
                            event={reasonTarget}
                            reasonCodes={filterOptions.reasonCodes || []}
                            onClose={() => { setReasonDialogOpen(false); setReasonTarget(null); }}
                            onSave={handleSaveReason}
                            saving={saving}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default ExecutiveReportPage;
